import { createClient } from '@supabase/supabase-js'

// Supabase credentials
const SUPABASE_URL = 'https://hofvrlixaxzukcuqmgpd.supabase.co'
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImhvZnZybGl4YXh6dWtjdXFtZ3BkIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTI1NzMyMjcsImV4cCI6MjA2ODE0OTIyN30.jcx5cxVaPPnJGr17eZFTv9LlE9Hzf5YfrUaESiFXTj8'

// Create Supabase client with specific iOS optimizations
const iOSSafari = typeof navigator !== 'undefined' && 
                 /iPad|iPhone|iPod/.test(navigator.userAgent) && 
                 !window.MSStream && 
                 /Safari/i.test(navigator.userAgent) && 
                 !/Chrome/i.test(navigator.userAgent);

// Create Supabase client with optimized configuration
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: false // Disable URL detection which can cause issues on mobile
  },
  global: {
    // Add headers to ensure API key is included in all requests
    headers: {
      'apikey': SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`
    },
    // Add shorter timeouts for iOS Safari
    fetch: (...args) => {
      const [url, options = {}] = args;
      const timeoutMs = iOSSafari ? 10000 : 30000; // 10 seconds for iOS Safari, 30 for others
      
      return new Promise((resolve, reject) => {
        const controller = new AbortController();
        const signal = controller.signal;
        const timeout = setTimeout(() => {
          controller.abort();
          reject(new Error(`Fetch timeout after ${timeoutMs}ms`));
        }, timeoutMs);
        
        fetch(url, {
          ...options,
          signal,
          headers: {
            ...options.headers,
            'apikey': SUPABASE_ANON_KEY, // Add API key to every request 
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, // Add auth header to every request 
            'Cache-Control': 'no-cache', // Prevent caching issues on iOS
            'Pragma': 'no-cache' // Additional header to prevent caching
          }
        })
          .then(resolve)
          .catch(reject)
          .finally(() => clearTimeout(timeout));
      });
    }
  }
});

// Function to safely execute a query with retry and improved error handling
const safeQuery = async (queryFn, maxRetries = 3) => {
  let attempts = 0;
  let lastError = null;
  
  while (attempts < maxRetries) {
    try {
      return await queryFn();
    } catch (error) {
      attempts++;
      lastError = error;
      console.error(`Query attempt ${attempts} failed:`, error);
      
      if (attempts >= maxRetries) {
        console.error(`All ${maxRetries} attempts failed. Last error:`, lastError);
        throw error;
      }
      
      // Exponential backoff: wait longer between each retry
      const delay = Math.min(500 * Math.pow(2, attempts - 1), 3000);
      console.log(`Retrying query in ${delay}ms (attempt ${attempts + 1}/${maxRetries})`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
};

// Function to fetch a specific report by ID
export const fetchReportById = async (orderId, merchantId) => {
  try {
    if (!orderId) {
      console.error("Missing orderId in fetchReportById");
      return null;
    }
    
    return await safeQuery(async () => {
      console.log(`Fetching report for orderId=${orderId}, merchantId=${merchantId || 'null'}`);
      
      let query = supabase
        .from('return_reports')
        .select('*')
        .eq('order_id', orderId);
      
      // Add merchant filter if provided
      if (merchantId) {
        query = query.eq('merchant_id', merchantId);
      }
      
      // Execute query with single result
      const { data, error } = await query.maybeSingle();
      
      if (error) {
        console.error(`Error fetching report for order ${orderId}:`, error);
        throw error; // Throw to trigger retry
      }
      
      // Normalize dates for iOS compatibility
      if (data) {
        data.created_at = normalizeDateString(data.created_at);
        data.created_at_vienna = normalizeDateString(data.created_at_vienna);
      }
      
      console.log(`Report data for ${orderId}:`, data ? 'found' : 'not found');
      return data;
    });
  } catch (err) {
    console.error(`Exception in fetchReportById for order ${orderId}:`, err);
    return null;
  }
};

// Helper function to normalize date strings
const normalizeDateString = (dateString) => {
  if (!dateString) return null;
  
  try {
    // Try to create a valid date object
    const date = new Date(dateString);
    
    // Check if date is valid
    if (!isNaN(date.getTime())) {
      return date.toISOString();
    }
    
    console.warn(`Invalid date format: ${dateString}`);
    return null;
  } catch (e) {
    console.error(`Error normalizing date: ${dateString}`, e);
    return null;
  }
};

// Function to fetch return reports with optional merchant filter
export const fetchReports = async (merchantId = null, storeId = null, locationNr = null) => {
  console.log("fetchReports called with:", { merchantId, storeId, locationNr });
  
  try {
    // Validate input parameters to prevent errors
    if (typeof merchantId !== 'string' && merchantId !== null) {
      console.error("Invalid merchantId type:", typeof merchantId);
      return [];
    }
    
    return await safeQuery(async () => {
      // Debug - print out the query we're about to run
      console.log(`Building query for merchant_id=${merchantId || 'null'}, store_id=${storeId || 'null'}, location_nr=${locationNr || 'null'}`);
      
      let query = supabase
        .from('return_reports')
        .select(`
          id,
          order_id,
          merchant_id,
          status,
          description,
          created_at_vienna,
          created_at,
          store_id,
          location_nr
        `)
        // IMPORTANT: Sort by created_at in descending order to show newest first
        // First sort by created_at_vienna (if available)
        .order('created_at_vienna', { ascending: false, nullsLast: true })
        // Then fall back to created_at
        .order('created_at', { ascending: false, nullsLast: true });
      
      // Apply filters if provided
      if (merchantId) {
        console.log("Applying merchant filter:", merchantId);
        query = query.eq('merchant_id', merchantId);
      }
      
      // Apply store_id filter if provided
      if (storeId) {
        console.log("Applying store filter:", storeId);
        query = query.eq('store_id', storeId);
      }
      
      // Apply location_nr filter if provided
      if (locationNr && locationNr !== '*') {
        console.log("Applying location filter:", locationNr);
        // Convert locationNr to string to ensure consistent comparison
        query = query.eq('location_nr', String(locationNr));
      }
      
      console.log("Executing Supabase query...");
      
      // Execute the query
      const { data, error } = await query;
      
      if (error) {
        console.error('Error fetching reports:', error);
        throw error; // Throw to trigger retry
      }
      
      // Debug output - important for troubleshooting
      console.log("Reports fetched:", data ? data.length : 0);
      if (data && data.length > 0) {
        console.log("First report:", data[0]);
        console.log("Date fields:", {
          first_created_at_vienna: data[0].created_at_vienna,
          first_created_at: data[0].created_at,
          last_created_at_vienna: data[data.length-1].created_at_vienna,
          last_created_at: data[data.length-1].created_at
        });
      } else {
        console.log("No reports found for the given filters");
      }
      
      // Normalize dates for iOS compatibility
      if (data && Array.isArray(data)) {
        for (let i = 0; i < data.length; i++) {
          // Normalize both date fields for every record
          data[i].created_at = normalizeDateString(data[i].created_at);
          data[i].created_at_vienna = normalizeDateString(data[i].created_at_vienna);
          
          // If both dates are null after normalization, use current time as fallback
          if (!data[i].created_at && !data[i].created_at_vienna) {
            console.warn(`Both date fields invalid for report ${data[i].order_id}, using current time as fallback`);
            data[i].created_at = new Date().toISOString();
          }
        }
        
        // Double check the sorting after date normalization
        data.sort((a, b) => {
          // Use created_at_vienna if available for both records
          if (a.created_at_vienna && b.created_at_vienna) {
            return new Date(b.created_at_vienna) - new Date(a.created_at_vienna);
          }
          
          // Fall back to created_at if created_at_vienna is not available for either record
          const dateA = a.created_at_vienna || a.created_at;
          const dateB = b.created_at_vienna || b.created_at;
          
          if (dateA && dateB) {
            return new Date(dateB) - new Date(dateA);
          }
          
          // If one record has a date and the other doesn't, the one with a date comes first
          if (dateA) return -1;
          if (dateB) return 1;
          
          // If neither has a date, maintain original order
          return 0;
        });
      }
      
      return Array.isArray(data) ? data : [];
    });
  } catch (err) {
    console.error('Exception in fetchReports:', err);
    return []; // Return empty array on error
  }
};

// Function to fetch photos for a specific report with improved caching and error handling
export const fetchReportPhotos = async (orderId, merchantId = null) => {
  try {
    if (!orderId) {
      console.error("Missing orderId in fetchReportPhotos");
      return [];
    }
    
    // Use a unique cache-busting parameter for iOS devices to prevent caching issues
    const cacheBuster = iOSSafari ? `&_cb=${Date.now()}` : '';
    
    return await safeQuery(async () => {
      console.log(`Fetching photos for orderId=${orderId}, merchantId=${merchantId || 'null'}`);
      
      let query = supabase
        .from('return_photos')
        .select('*')
        .eq('order_id', orderId)
        .order('created_at', { ascending: false }); // Sort photos newest first
      
      // Filter by merchant if provided
      if (merchantId) {
        query = query.eq('merchant_id', merchantId);
      }
      
      // Limit to just a few photos on iOS
      if (iOSSafari) {
        query = query.limit(3);
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error(`Error fetching photos for order ${orderId}:`, error);
        throw error; // Throw to trigger retry
      }
      
      console.log(`Found ${data ? data.length : 0} photos for order ${orderId}`);
      return Array.isArray(data) ? data : [];
    });
  } catch (err) {
    console.error(`Exception in fetchReportPhotos for order ${orderId}:`, err);
    return []; // Return empty array on error
  }
};

// Function to get a public URL for a photo with improved cache busting for iOS
export const getPhotoUrl = async (filePath) => {
  try {
    if (!filePath) {
      console.error('No file path provided to getPhotoUrl');
      return null;
    }
    
    return await safeQuery(async () => {
      console.log(`Getting photo URL for path: ${filePath}`);
      
      // Add cache-busting parameter for iOS devices
      const cacheBuster = iOSSafari ? `?_cb=${Date.now()}` : '';
      
      const { data } = await supabase.storage
        .from('return-photos')
        .getPublicUrl(filePath);
      
      let url = data?.publicUrl || null;
      
      // Add cache buster to URL if on iOS
      if (url && iOSSafari && !url.includes('?')) {
        url = `${url}${cacheBuster}`;
      }
      
      console.log(`Photo URL generated: ${url ? 'success' : 'failed'}`);
      return url;
    });
  } catch (err) {
    console.error(`Exception in getPhotoUrl for path ${filePath}:`, err);
    return null;
  }
};

// Function to fetch all merchants
export const fetchMerchants = async () => {
  try {
    return await safeQuery(async () => {
      const { data, error } = await supabase
        .from('merchant_configurations')
        .select('merchant_id, name')
        .order('name');
      
      if (error) {
        console.error('Error fetching merchants:', error);
        return [];
      }
      
      return Array.isArray(data) ? data : [];
    });
  } catch (err) {
    console.error('Exception in fetchMerchants:', err);
    return [];
  }
};

// Export debug info with device detection
export const getDebugInfo = () => {
  return {
    supabaseUrl: SUPABASE_URL,
    isIOS: iOSSafari,
    userAgent: navigator?.userAgent || 'unknown',
    timestamp: new Date().toISOString()
  };
};

export default supabase;